import { Select as ADSelect } from "antd";
import { useMemo, useCallback } from "react";

/**
 * Common Select Component with label search support
 * @param {import('antd').SelectProps & {
 *   searchByLabel?: boolean,
 *   caseSensitive?: boolean
 * }} props
 * @property {boolean} [searchByLabel=true] - Enable searching by label
 * @property {boolean} [caseSensitive=false] - Enable case-sensitive search
 */
const Select = ({
  searchByLabel = true,
  caseSensitive = false,
  showSearch,
  children,
  ...restProps
}) => {
  // Default filter function with memoization
  const defaultFilterOption = useCallback(
    (input, option) => {
      if (!input) return true;

      const searchText = caseSensitive ? input : input.toLowerCase();

      // Get label from option
      const label = option?.label ?? option?.children ?? "";
      const labelText = typeof label === "string" ? label : String(label);
      const processedLabel = caseSensitive
        ? labelText
        : labelText.toLowerCase();

      // Get value from option
      const value = option?.value?.toString() ?? "";
      const processedValue = caseSensitive ? value : value.toLowerCase();

      return (
        processedLabel.includes(searchText) ||
        processedValue.includes(searchText)
      );
    },
    [caseSensitive]
  );

  // Compute final props with memoization
  const selectProps = useMemo(() => {
    //@ts-ignore
    const { filterOption, optionFilterProp, ...restSearchProps } =
      showSearch || {};

    if (!searchByLabel)
      return {
        showSearch,
        filterOption,
        optionFilterProp,
        restSearchProps,
      };

    return {
      showSearch: true,
      filterOption: filterOption ?? defaultFilterOption,
      optionFilterProp: optionFilterProp ?? "children",
      ...restSearchProps,
    };
  }, [searchByLabel, showSearch, defaultFilterOption]);

  return (
    <ADSelect showSearch={{ ...selectProps }} {...restProps}>
      {children}
    </ADSelect>
  );
};

export default Select;
